import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { BottomActionBar } from 'components/UI/BottomActionBar';
import Chip from '@material-ui/core/Chip';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Column } from 'components/UI/Column';
import { Row } from 'components/UI/Row';
import { ButtonLoader } from 'components/Loaders/ButtonLoader';
import { UNAUTHORIZED, FORBIDDEN, UNPROCESSABLE_ENTITY } from 'http-status-codes';
import { aclPrefix, aclUsersGroupsPrefix } from 'routes/routes';
import { axiosInstance } from '../../axiosInstance';

// TODO: unify with other user types
type User = {
  company_name: string;
  email: string;
  full_name: string;
  id: string;
};

export enum AllowedUsers {
  Any = 'any',
  AnyRegistered = 'any_registered',
  SomeRegistered = 'some_registered'
}

export type UserGroupFormData = {
  allowed_users: string;
  name: string;
};

const UserGroupFormSchema = object().shape({
  name: string().required('acl.user_group.errors.name required')
});

const UserGroupForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { userGroupId } = useParams<{ userGroupId: string }>();
  const [typeUser, setTypeUser] = useState<string>('');
  const [added, setAdded] = useState<string[]>([]);
  const [savedUsers, setSavedUsers] = useState<User[]>([]);
  // TODO: hook
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [savingError, setSavingError] = useState<string>('');
  // END TODO
  const { handleSubmit, errors, control, setValue, watch } = useForm<UserGroupFormData>({
    mode: 'onBlur',
    resolver: yupResolver(UserGroupFormSchema),
    defaultValues: {
      allowed_users: AllowedUsers.Any
    }
  });

  const watchAllowedUsers = watch('allowed_users');

  // TODO: hook management or just move out from this
  useEffect(() => {
    const loadUserGroup = async () => {
      setIsLoading(true);
      try {
        const {
          data: { allowed_users, name, users }
        } = await axiosInstance.get(`/acls/user-groups/${userGroupId}`);
        setTimeout(() => {
          setValue('name', name);
          setValue('allowed_users', allowed_users);
          setSavedUsers(users);
        });
      } catch (e) {
        console.log(e);
      } finally {
        setIsLoading(false);
      }
    };
    if (userGroupId) {
      loadUserGroup();
    }
  }, []);

  const onSubmit = async (formData: UserGroupFormData) => {
    // manage errors
    const { name, allowed_users } = formData;
    const body = {
      name,
      allowed_users,
      users: [...added, ...savedUsers.map(su => su.email)]
    };

    // TODO: hook management or just move out from this
    try {
      if (userGroupId) {
        setIsSaving(true);
        const {
          data: { users }
        } = await axiosInstance.put(`/acls/user-groups/${userGroupId}`, body);
        setSavedUsers(users);
      } else {
        const {
          data: { users, id }
        } = await axiosInstance.post('/acls/user-groups', body);
        setSavedUsers(users);
        history.replace(`${aclPrefix}${aclUsersGroupsPrefix}/${id}`);
      }
      setAdded([]);
    } catch (e) {
      if (!e.response) {
        setSavingError(t('errors.something went wrong'));
        return;
      }
      const { status } = e.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        setSavingError(t('errors.unauthorized'));
        return;
      }

      if (status === UNPROCESSABLE_ENTITY) {
        setSavingError(t('acl.user_group.errors.user not registered on boxe'));
        return;
      }

      setSavingError(t('errors.something went wrong'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Row justifyContent="center" alignItems="center">
        <CircularProgress color="primary" size={34} />
      </Row>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        onKeyDown={e => {
          if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault();
            return false;
          }
          return true;
        }}
      >
        <Controller
          as={
            <TextField
              fullWidth
              autoComplete="new-password"
              helperText={t(get(errors, 'name.message'))}
              error={!isEmpty(get(errors, `name`))}
              label={t('acl.users group name')}
              style={{ marginBottom: '40px' }}
            />
          }
          control={control}
          defaultValue=""
          name="name"
        />

        <FormControl variant="outlined" style={{ marginBottom: '20px' }}>
          <InputLabel id="account-type-label">{t('acl.user_group.select associated users')}</InputLabel>
          <Controller
            as={
              <Select
                labelId="account-type-label"
                input={<OutlinedInput label={t('acl.user_group.select associated users')} style={{ width: '400px' }} />}
              >
                <MenuItem value={AllowedUsers.Any}>{t('acl.user_group.type any')}</MenuItem>
                <MenuItem value={AllowedUsers.AnyRegistered}>{t('acl.user_group.type any registered')}</MenuItem>
                <MenuItem value={AllowedUsers.SomeRegistered}>{t('acl.user_group.type some registered')}</MenuItem>
              </Select>
            }
            control={control}
            defaultValue=""
            name="allowed_users"
          />
        </FormControl>

        {watchAllowedUsers === AllowedUsers.SomeRegistered && (
          <>
            <Column>
              <h5 style={{ marginTop: '20px' }}>{t('acl.users')}</h5>
              <Row alignItems="center" margin="0 0 15px 0">
                <TextField
                  autoComplete="new-password"
                  label={t('acl.user_group.add user email')}
                  variant="outlined"
                  onChange={e => setTypeUser(e.target.value)}
                  onKeyUp={e => {
                    if ((e.key === 'Enter' || e.keyCode === 13) && !isEmpty(typeUser)) {
                      setAdded([...added, typeUser]);
                      setTypeUser('');
                    }
                  }}
                  value={typeUser}
                  style={{ width: '400px' }}
                />
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={() => setAdded([...added, typeUser])}
                  style={{ marginLeft: '20px', width: '100px' }}
                >
                  add
                </Button>
              </Row>

              <h5 style={{ marginTop: '20px' }}>{t('acl.user_group.associated users')}</h5>
              <Row gap="0 10px">
                {savedUsers.map((su: User) => (
                  <Chip
                    key={su.id}
                    label={su.email}
                    variant="outlined"
                    onDelete={() => {
                      setSavedUsers(savedUsers.filter(s => s.id !== su.id));
                    }}
                  />
                ))}
                <span>{isEmpty(savedUsers) && '-'}</span>
              </Row>

              {added.length > 0 && <h5 style={{ marginTop: '20px' }}>{t('acl.user_group.added users')}</h5>}
              <Row gap="0 10px">
                {added.map((a: string) => (
                  <Chip
                    key={a}
                    label={a}
                    variant="outlined"
                    onDelete={() => {
                      setAdded(added.filter(e => e !== a));
                    }}
                  />
                ))}
              </Row>
            </Column>
          </>
        )}

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          key="bottom,center"
          open={!isEmpty(savingError)}
          autoHideDuration={5000}
          onClose={() => {
            setSavingError('');
          }}
          style={{ bottom: '10px' }}
        >
          <Alert severity="error" variant="filled" onClose={() => setSavingError('')}>
            {savingError}
          </Alert>
        </Snackbar>

        <BottomActionBar>
          <Row alignItems="center" justifyContent="flex-end" margin="0 40px 0 0">
            <Button color="primary" disabled={isSaving} variant="contained" type="submit">
              {t('label.save')}
              <ButtonLoader show={isSaving} />
            </Button>
          </Row>
        </BottomActionBar>
      </form>
    </>
  );
};

export { UserGroupForm };
